/**
 * API Headers Utilities
 * 
 * Provides authentication headers for REST API calls (file uploads, etc.)
 * For GraphQL, use URQL client which handles headers automatically.
 */

import { getInitData } from "./telegram";
import { getStoredTokens, isTokenExpired } from "./storage";

/**
 * Get authorization headers for API requests.
 * Use this for REST endpoints (uploads, admin actions, etc.)
 * 
 * Priority: Telegram initData > JWT Bearer token
 * 
 * @returns Headers object for fetch requests
 * 
 * @example
 * ```ts
 * const response = await fetch(url, {
 *   method: "POST",
 *   headers: getAuthHeaders(),
 *   body: formData,
 * });
 * ```
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    // CSRF defense-in-depth
    "X-Requested-With": "XMLHttpRequest",
  };

  // Use Telegram initData if available (primary auth in Mini App)
  const initData = getInitData();
  if (initData) {
    headers["x-telegram-init-data"] = initData;
    return headers;
  }

  // Fallback to JWT token for PWA mode
  const tokens = getStoredTokens();
  if (tokens && !isTokenExpired(tokens.expiresAt)) {
    headers["Authorization"] = `Bearer ${tokens.accessToken}`;
  }

  return headers;
}

/**
 * Get headers for JSON API requests.
 * Includes Content-Type and auth headers.
 */
export function getJsonHeaders(): Record<string, string> {
  return {
    ...getAuthHeaders(),
    "Content-Type": "application/json",
  };
}
